import { useState } from 'react';
import { useScrollAnimation } from '../../hooks/useScrollAnimation';
import { BUSINESS } from '../../mocks/sharedData';

interface FinalCTASectionProps {
  headline?: string;
  subheadline?: string;
  primaryCTA?: string;
  showForm?: boolean;
}

const FORM_ENDPOINT = 'https://readdy.ai/api/form/d724tcghb0rqicn68s90';

const FinalCTASection = ({
  headline = 'Ready to Eliminate Your Electric Bill?',
  subheadline = 'Get a free, no-obligation solar estimate from Kentuckiana\'s most trusted family-owned installer.',
  primaryCTA = 'Schedule Free Consultation',
  showForm = true,
}: FinalCTASectionProps) => {
  const { ref, isVisible } = useScrollAnimation();
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setSubmitting(true);
    const form = e.currentTarget;
    const data = new URLSearchParams(new FormData(form) as unknown as URLSearchParams);
    try {
      await fetch(FORM_ENDPOINT, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: data.toString(),
      });
      setSubmitted(true);
    } catch {
      setSubmitted(true);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section className="relative py-32 overflow-hidden" id="estimate">
      {/* Full-bleed background */}
      <div className="absolute inset-0">
        <img
          src="https://public.readdy.ai/ai/img_res/be3ea9ef3603ce1662383448ab36ae6f.jpg"
          alt="Premium solar home at sunset"
          className="w-full h-full object-cover object-center"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-zinc-950/98 via-zinc-950/92 to-zinc-950/70" />
        <div className="absolute inset-0 bg-zinc-950/40" />
      </div>

      {/* Ambient glow */}
      <div className="absolute top-1/2 left-1/4 w-96 h-96 bg-amber-500/10 rounded-full blur-3xl pointer-events-none" />

      <div className="relative z-10 max-w-7xl mx-auto px-6">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-start">
          {/* Left — CTA copy */}
          <div
            ref={ref}
            className={`transition-all duration-700 ${
              isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-8'
            }`}
          >
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-px bg-amber-500" />
              <span className="text-amber-400 text-xs font-semibold tracking-[0.25em] uppercase">
                Get Your Free Estimate Today
              </span>
            </div>
            <h2 className="font-serif text-5xl md:text-6xl lg:text-7xl text-white leading-tight mb-6">{headline}</h2>
            <p className="text-zinc-300 text-xl leading-relaxed mb-10">{subheadline}</p>

            {/* Benefit list */}
            <ul className="space-y-4 mb-10">
              {[
                'Free custom solar system design for your property',
                'Personalized savings estimate — see your exact ROI',
                'Full 30% federal tax credit guidance',
                'Response within 2 business days',
                'Zero pressure, zero obligation',
              ].map((item) => (
                <li key={item} className="flex items-center gap-3">
                  <div className="w-6 h-6 flex items-center justify-center bg-amber-500 rounded-full flex-shrink-0">
                    <i className="ri-check-line text-zinc-950 text-xs font-bold" />
                  </div>
                  <span className="text-zinc-300 text-base">{item}</span>
                </li>
              ))}
            </ul>

            {/* Phone CTA */}
            <a
              href={BUSINESS.phoneTel}
              className="inline-flex items-center gap-4 bg-zinc-800/80 hover:bg-zinc-700/80 border border-zinc-700 hover:border-amber-500/50 text-white font-semibold px-7 py-5 rounded-full text-sm whitespace-nowrap transition-all duration-300 cursor-pointer backdrop-blur-sm"
            >
              <div className="w-10 h-10 flex items-center justify-center bg-amber-500 rounded-full flex-shrink-0">
                <i className="ri-phone-fill text-zinc-950 text-base" />
              </div>
              <div>
                <p className="text-xs text-zinc-400 leading-none mb-0.5">Call Us Directly</p>
                <p className="text-base font-bold">{BUSINESS.phone}</p>
              </div>
            </a>
          </div>

          {/* Right — Premium form */}
          {showForm && (
            <div
              className={`transition-all duration-700 delay-200 ${
                isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-8'
              }`}
            >
              <div className="bg-white rounded-3xl overflow-hidden">
                {/* Form header */}
                <div className="bg-amber-500 px-8 py-6">
                  <h3 className="font-serif text-2xl text-zinc-950 font-bold mb-1">Request a Free Estimate</h3>
                  <p className="text-zinc-800 text-sm">We&apos;ll respond within 2 business days.</p>
                </div>

                <div className="p-8">
                  {submitted ? (
                    <div className="text-center py-8">
                      <div className="w-16 h-16 flex items-center justify-center bg-amber-100 rounded-full mx-auto mb-4">
                        <i className="ri-check-double-line text-amber-600 text-2xl" />
                      </div>
                      <h3 className="font-serif text-2xl text-zinc-900 mb-3">Request Received!</h3>
                      <p className="text-zinc-500 text-sm mb-6">
                        Thank you! A Star Solar specialist will contact you within 2 business days.
                      </p>
                      <a
                        href={BUSINESS.consultationUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-block bg-amber-500 hover:bg-amber-400 text-zinc-950 font-bold px-8 py-4 rounded-full text-sm whitespace-nowrap cursor-pointer transition-colors"
                      >
                        {primaryCTA}
                      </a>
                    </div>
                  ) : (
                    <form onSubmit={handleSubmit} data-readdy-form className="space-y-4">
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <label className="text-zinc-600 text-xs font-semibold mb-1.5 block">First Name *</label>
                          <input
                            name="firstname"
                            type="text"
                            required
                            className="w-full bg-stone-50 border border-stone-200 rounded-xl px-4 py-3 text-zinc-900 text-sm placeholder-zinc-400 focus:outline-none focus:border-amber-500 transition-colors"
                            placeholder="John"
                          />
                        </div>
                        <div>
                          <label className="text-zinc-600 text-xs font-semibold mb-1.5 block">Last Name *</label>
                          <input
                            name="lastname"
                            type="text"
                            required
                            className="w-full bg-stone-50 border border-stone-200 rounded-xl px-4 py-3 text-zinc-900 text-sm placeholder-zinc-400 focus:outline-none focus:border-amber-500 transition-colors"
                            placeholder="Smith"
                          />
                        </div>
                      </div>

                      <div>
                        <label className="text-zinc-600 text-xs font-semibold mb-1.5 block">Email Address *</label>
                        <input
                          name="email"
                          type="email"
                          required
                          className="w-full bg-stone-50 border border-stone-200 rounded-xl px-4 py-3 text-zinc-900 text-sm placeholder-zinc-400 focus:outline-none focus:border-amber-500 transition-colors"
                          placeholder="john@example.com"
                        />
                      </div>

                      <div>
                        <label className="text-zinc-600 text-xs font-semibold mb-1.5 block">Phone Number *</label>
                        <input
                          name="phone"
                          type="tel"
                          required
                          className="w-full bg-stone-50 border border-stone-200 rounded-xl px-4 py-3 text-zinc-900 text-sm placeholder-zinc-400 focus:outline-none focus:border-amber-500 transition-colors"
                          placeholder="(812) 555-0123"
                        />
                      </div>

                      <div>
                        <label className="text-zinc-600 text-xs font-semibold mb-1.5 block">Property Address *</label>
                        <input
                          name="full_address"
                          type="text"
                          required
                          className="w-full bg-stone-50 border border-stone-200 rounded-xl px-4 py-3 text-zinc-900 text-sm placeholder-zinc-400 focus:outline-none focus:border-amber-500 transition-colors"
                          placeholder="123 Main St, Jeffersonville, IN"
                        />
                      </div>

                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <label className="text-zinc-600 text-xs font-semibold mb-1.5 block">Property Type</label>
                          <select
                            name="property_type"
                            className="w-full bg-stone-50 border border-stone-200 rounded-xl px-4 py-3 text-sm text-zinc-700 focus:outline-none focus:border-amber-500 transition-colors cursor-pointer"
                          >
                            <option value="">Select type</option>
                            <option value="Residential">Residential</option>
                            <option value="Commercial">Commercial</option>
                            <option value="Nonprofit">Nonprofit</option>
                          </select>
                        </div>
                        <div>
                          <label className="text-zinc-600 text-xs font-semibold mb-1.5 block">Battery Storage?</label>
                          <select
                            name="are_you_interested_in_battery_storage_"
                            className="w-full bg-stone-50 border border-stone-200 rounded-xl px-4 py-3 text-sm text-zinc-700 focus:outline-none focus:border-amber-500 transition-colors cursor-pointer"
                          >
                            <option value="">Select</option>
                            <option value="Yes">Yes</option>
                            <option value="No">No</option>
                            <option value="Not Sure">Not Sure</option>
                          </select>
                        </div>
                      </div>

                      <button
                        type="submit"
                        disabled={submitting}
                        className="w-full bg-amber-500 hover:bg-amber-400 disabled:opacity-60 text-zinc-950 font-bold py-4 rounded-full text-sm whitespace-nowrap transition-all duration-300 cursor-pointer mt-2 tracking-wide"
                      >
                        {submitting ? 'Submitting...' : 'Get My Free Solar Estimate'}
                      </button>

                      <p className="text-center text-zinc-400 text-xs">
                        No spam. No pressure. 100% free consultation.
                      </p>
                    </form>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </section>
  );
};

export default FinalCTASection;
